import React, { useState } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { ROUTES } from '../routes/routes';
import { useAuth } from '../contexts/AuthContext';
import { API_ORIGIN } from '../api';
import './css/VerifyEmailPage.css';
import './css/LoginPage.css'; // Import shared login page styles

const VerifyEmailPage = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  // Get email from location state or localStorage
  const storedEmail = location.state?.email || localStorage.getItem('pendingVerificationEmail');
  const [email, setEmail] = useState(storedEmail || '');
  const [showEmailInput] = useState(!storedEmail);

  const handleChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    // Check if it's a 6-digit code
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setCode(newCode);
      setError('');
      // Focus last input
      const lastInput = document.getElementById('code-5');
      if (lastInput) lastInput.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Email not found. Please try signing up again.');
      return;
    }

    const verificationCode = code.join('');
    if (verificationCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_ORIGIN}/v1/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          code: verificationCode,
        }),
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned an invalid response. Please try again later.');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Verification failed');
      }

      // Auto-login user after verification
      if (data.tokens?.access?.token) {
        login(data.tokens.access.token, data.user || null);
        // Store refresh token if available
        if (data.tokens.refresh?.token) {
          localStorage.setItem('refreshToken', data.tokens.refresh.token);
        }
      }
      localStorage.removeItem('pendingVerificationEmail');

      // Redirect to app
      navigate(ROUTES.App(), { replace: true });
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setError('Email not found. Please try signing up again.');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_ORIGIN}/v1/auth/resend-verification-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned an invalid response. Please try again later.');
      }

      if (!response.ok) {
        throw new Error('Failed to resend code');
      }

      // Clear current code
      setCode(['', '', '', '', '', '']);
      setError('');
      
      // Show success message
      setSuccess('A new verification code has been sent to your email!');
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    localStorage.removeItem('pendingVerificationEmail');
    navigate(ROUTES.Home());
  };

  return (
    <div className="login-container">
      <div className="login-left-column">
        <div className="brand-container">
          <NavLink to={ROUTES.Home()} className="brand-title">
            <img src="/white_logo.png" alt="Render FM Logo" className="brand-logo" />
            <h1>Render FM</h1>
          </NavLink>
          <p className="brand-tagline">
            Taking Audio Visualisations up a notch
          </p>
        </div>
      </div>
      <div className="login-right-column">
        <div className="login-brand-header">
          <NavLink to={ROUTES.Home()} className="login-brand-title">
            <img src="/black_logo.png" alt="Render FM Logo" className="login-brand-logo" />
            Render FM
          </NavLink>
        </div>
        <div className="login-top-menu-mobile">
          <div className="login-top-menu-container">
            <NavLink to={ROUTES.Home()} className="login-top-menu-brand">
              <img src="/pink_logo.png" alt="Render FM Logo" className="login-mobile-logo" />
              Render FM
            </NavLink>
          </div>
        </div>
        <div className="verify-email-form-container">
          <button 
            className="verify-email-close" 
            onClick={handleClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
          
          <div className="verify-email-form-header">
            <h2 className="verify-email-form-title">Verify Your Email</h2>
            {email ? (
              <p className="verify-email-form-subtitle">
                We've sent a 6-digit verification code to <strong>{email}</strong>
              </p>
            ) : (
              <p className="verify-email-form-subtitle">
                Please enter your email address and verification code
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="verify-email-form">
            {showEmailInput && (
              <div className="verify-email-input-container">
                <label htmlFor="email" className="verify-email-form-label">Email Address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="verify-email-form-input"
                  required
                />
              </div>
            )}
            
            <div className="code-inputs">
              {code.map((digit, index) => (
                <input
                  key={index}
                  id={`code-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="code-input"
                  disabled={isLoading}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {error && <p className="verify-email-form-error">{error}</p>}
            {success && <p className="verify-email-form-success">{success}</p>}

            <button
              type="submit"
              className="verify-email-form-button"
              disabled={isLoading || code.some((d) => !d)}
            >
              {isLoading ? 'Verifying...' : 'Verify Email'}
            </button>

            <div className="verify-email-form-footer">
              <p>Didn't receive the code?</p>
              <button
                type="button"
                onClick={handleResend}
                className="verify-email-resend-button"
                disabled={isLoading}
              >
                Resend Code
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;

